/**
 * Hook useAvaliacoes
 * Gerencia todas as operações relacionadas a avaliações de proficiência
 */

import { useState, useCallback } from "react";
import { avaliacoesService } from "../services";
import { useAuth } from "./useAuth";
import { getAtividadePedagogicaById } from "../constants/atividadesPedagogicas";

/**
 * Tipos de avaliação
 */
export const TIPOS_AVALIACAO = {
  ENTRADA: "ENTRADA",
  PERIODICA: "PERIODICA",
  SAIDA: "SAIDA",
};

/**
 * Disciplinas (legado — qualquer atividade pedagógica é válida)
 */
export const DISCIPLINAS = {
  PORTUGUES: "PORTUGUES",
  MATEMATICA: "MATEMATICA",
};

/**
 * Quantidade de diagnósticos pedagógicos por disciplina/turma
 */
export const NUMERO_DIAGNOSTICOS = 2;

/**
 * Títulos padrão dos diagnósticos (índice 1 e 2)
 */
export const TITULOS_DIAGNOSTICO = {
  1: "Diagnóstico inicial",
  2: "Diagnóstico final",
};

/**
 * Níveis de proficiência
 */
export const NIVELS_PROFICIENCIA = {
  NIVEL_1: 1,
  NIVEL_2: 2,
  NIVEL_3: 3,
  NIVEL_4: 4,
};

/**
 * Labels dos níveis (planilha EducaPaço)
 */
export const NIVEIS_LABELS = {
  1: "Crítico",
  2: "Básico",
  3: "Intermediário",
  4: "Adequado",
};

/**
 * Faixas de nível genéricas (aplicam a qualquer disciplina pedagógica)
 */
export const FAIXAS_NIVEL = {
  1: { min: 0, max: 25 },
  2: { min: 26, max: 50 },
  3: { min: 51, max: 75 },
  4: { min: 76, max: 100 },
};

/**
 * Retorna o label do nível (Crítico, Básico, ...)
 */
export const getNivelLabel = nivel => NIVEIS_LABELS[nivel] || `Nível ${nivel}`;

/**
 * Retorna o nome amigável da disciplina/atividade pedagógica
 */
export const getDisciplinaLabel = disciplinaId => {
  if (!disciplinaId) return "";
  const atividade = getAtividadePedagogicaById(String(disciplinaId).toUpperCase());
  if (atividade?.nome) return atividade.nome;
  const legadas = {
    PORTUGUES: "Português",
    MATEMATICA: "Matemática",
  };
  const key = String(disciplinaId).toUpperCase();
  return legadas[key] || disciplinaId;
};

/**
 * Lê total_questoes do gabarito simplificado
 */
export const getTotalQuestoesFromGabarito = gabarito => {
  if (!Array.isArray(gabarito) || gabarito.length === 0) return null;
  const total = gabarito[0]?.total_questoes;
  if (total === undefined || total === null || total === "") return null;
  const num = Number(total);
  return Number.isFinite(num) && num > 0 ? num : null;
};

/**
 * Chave estável de ordenação de diagnósticos (mesma regra no lançamento e no histórico)
 * 1ª = Diagnóstico inicial, 2ª = Diagnóstico final
 *
 * Importante: o histórico via RPC `buscar_avaliacoes_historico_aluno` não retorna
 * criado_em — só data_aplicacao. Por isso priorizamos data_aplicacao para as duas
 * telas ficarem iguais. Desempate por id da avaliação.
 */
export const getAvaliacaoSortMeta = avaliacao => {
  if (!avaliacao) return { time: 0, id: "" };
  const raw =
    avaliacao.data_aplicacao ||
    avaliacao.dataAplicacao ||
    avaliacao.data ||
    avaliacao.criado_em ||
    avaliacao.criadoEm ||
    avaliacao.created_at ||
    avaliacao.createdAt ||
    0;
  const time = new Date(raw).getTime();
  return {
    time: Number.isFinite(time) ? time : 0,
    id: String(
      avaliacao.avaliacaoId ||
        avaliacao.avaliacao_id ||
        avaliacao.id ||
        ""
    ),
  };
};

export const compareAvaliacoesDiagnostico = (a, b) => {
  const metaA = getAvaliacaoSortMeta(a);
  const metaB = getAvaliacaoSortMeta(b);
  if (metaA.time !== metaB.time) return metaA.time - metaB.time;
  return metaA.id.localeCompare(metaB.id);
};

/**
 * Ordena avaliações pela mesma regra do histórico (data_aplicacao + id)
 */
export const ordenarDiagnosticos = (avaliacoes = []) => {
  return [...(avaliacoes || [])].sort(compareAvaliacoesDiagnostico);
};

const textoDiagnostico = av => `${av?.titulo || ""} ${av?.descricao || ""}`;
const isTituloDiagnosticoInicial = av => /diagn[oó]stico\s*inicial/i.test(textoDiagnostico(av));
const isTituloDiagnosticoFinal = av =>
  /diagn[oó]stico\s*final/i.test(textoDiagnostico(av)) && !isTituloDiagnosticoInicial(av);

/**
 * Escolhe o par [inicial, final] para lançamento.
 * PRIORIDADE: títulos já gravados (evita trocar colunas quando a data é a mesma
 * e o desempate por id inverte a ordem). Só depois usa data/resultados.
 */
export const selecionarParDiagnostico = (avaliacoes = [], resultadosMap = {}) => {
  const lista = ordenarDiagnosticos(avaliacoes);
  if (lista.length === 0) return [];

  const getId = av => String(av.id || av.avaliacao_id || "");

  const iniciais = lista.filter(isTituloDiagnosticoInicial);
  const finais = lista.filter(isTituloDiagnosticoFinal);

  // 1) Títulos únicos e distintos — fonte da verdade (não reordenar por data/id)
  if (iniciais.length === 1 && finais.length === 1 && getId(iniciais[0]) !== getId(finais[0])) {
    const ini = iniciais[0];
    const fin = finais[0];
    const notasIni = (resultadosMap[getId(ini)] || []).length;
    const notasFin = (resultadosMap[getId(fin)] || []).length;
    // Recuperação: notas só no "final", inicial vazio, mesma data → rename anterior inverteu
    if (notasIni === 0 && notasFin > 0) {
      const metaIni = getAvaliacaoSortMeta(ini);
      const metaFin = getAvaliacaoSortMeta(fin);
      if (metaFin.time === metaIni.time) {
        return [fin, ini];
      }
    }
    return [ini, fin];
  }

  // 2) Só um título explícito: completa o par com a outra avaliação
  if (iniciais.length === 1) {
    const ini = iniciais[0];
    const outras = lista.filter(av => getId(av) !== getId(ini));
    if (lista.length <= NUMERO_DIAGNOSTICOS) {
      return outras.length ? [ini, outras[0]] : [ini];
    }
    const comNotas = outras.filter(av => (resultadosMap[getId(av)] || []).length > 0);
    const final = comNotas.length ? comNotas[comNotas.length - 1] : outras[outras.length - 1];
    return final ? [ini, final] : [ini];
  }

  if (finais.length === 1) {
    const fin = finais[0];
    const outras = lista.filter(av => getId(av) !== getId(fin));
    if (lista.length <= NUMERO_DIAGNOSTICOS) {
      return outras.length ? [outras[0], fin] : [fin];
    }
    const comNotas = outras.filter(av => (resultadosMap[getId(av)] || []).length > 0);
    const ini = comNotas.length ? comNotas[0] : outras[0];
    return ini ? [ini, fin] : [fin];
  }

  // 3) Sem títulos confiáveis: 2 ou menos → ordem cronológica
  if (lista.length <= NUMERO_DIAGNOSTICOS) {
    return lista.slice(0, NUMERO_DIAGNOSTICOS);
  }

  // 4) 3+ sem títulos: avaliações com notas (mais antiga + mais recente)
  const comNotas = lista.filter(av => (resultadosMap[getId(av)] || []).length > 0);
  if (comNotas.length >= NUMERO_DIAGNOSTICOS) {
    return [comNotas[0], comNotas[comNotas.length - 1]];
  }

  return [lista[0], lista[lista.length - 1]];
};

/**
 * Monta gabarito simplificado com total de questões
 */
export const buildGabaritoDiagnostico = (totalQuestoes = null) => [
  {
    questao: "Avaliação diagnóstica",
    resposta_correta: "N/A",
    peso: 1.0,
    ...(totalQuestoes != null ? { total_questoes: Number(totalQuestoes) } : {}),
  },
];

/**
 * Hook para gerenciar avaliações
 */
export const useAvaliacoes = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Calcula o nível de proficiência baseado no score percentual (0-100)
   * Faixas iguais para qualquer disciplina pedagógica
   * @param {number} score - Score percentual (0-100)
   * @returns {number} Nível de 1 a 4
   */
  const calculateNivel = useCallback(score => {
    for (let nivel = 4; nivel >= 1; nivel--) {
      const faixa = FAIXAS_NIVEL[nivel];
      if (score >= faixa.min && score <= faixa.max) {
        return nivel;
      }
    }

    return 1;
  }, []);

  /**
   * Calcula o score percentual baseado em acertos e pesos
   * @param {number} acertos - Número de acertos
   * @param {number} totalQuestoes - Total de questões
   * @param {Array<number>} pesos - Array de pesos por questão (opcional)
   * @param {Array<boolean>} respostas - Array de respostas (true = acerto, false = erro)
   * @returns {number} Score percentual (0-100)
   */
  const calculateScore = useCallback((acertos, totalQuestoes, pesos = null, respostas = null) => {
    if (pesos && respostas) {
      // Calcula com pesos
      let totalPeso = 0;
      let pesoAcertos = 0;

      respostas.forEach((acertou, index) => {
        const peso = pesos[index] || 1;
        totalPeso += peso;
        if (acertou) {
          pesoAcertos += peso;
        }
      });

      return totalPeso > 0 ? (pesoAcertos / totalPeso) * 100 : 0;
    } else {
      // Calcula simples (acertos / total)
      return totalQuestoes > 0 ? (acertos / totalQuestoes) * 100 : 0;
    }
  }, []);

  /**
   * Cria uma nova avaliação
   * @param {Object} avaliacaoData - Dados da avaliação
   * @param {string} avaliacaoData.turmaId - ID da turma
   * @param {string} avaliacaoData.tipo - Tipo (ENTRADA, PERIODICA, SAIDA)
   * @param {string} avaliacaoData.disciplina - Disciplina (PORTUGUES, MATEMATICA)
   * @param {string} avaliacaoData.titulo - Título da avaliação
   * @param {string} avaliacaoData.descricao - Descrição opcional
   * @param {Date|string} avaliacaoData.dataAplicacao - Data de aplicação
   * @param {Array<Object>} avaliacaoData.gabarito - Array de questões com gabarito e pesos
   * @returns {Promise<Object>} Avaliação criada
   */
  const createAvaliacao = useCallback(
    async avaliacaoData => {
      setLoading(true);
      setError(null);

      try {
        // Validações
        if (!avaliacaoData.turmaId) {
          throw new Error("Turma é obrigatória");
        }
        if (!avaliacaoData.tipo || !Object.values(TIPOS_AVALIACAO).includes(avaliacaoData.tipo)) {
          throw new Error("Tipo de avaliação inválido");
        }
        if (!avaliacaoData.disciplina || String(avaliacaoData.disciplina).trim() === "") {
          throw new Error("Disciplina inválida");
        }
        if (!avaliacaoData.gabarito || avaliacaoData.gabarito.length === 0) {
          throw new Error("Gabarito é obrigatório");
        }

        // Transforma o gabarito do formato do frontend para o formato do backend
        // Frontend: { numero, peso, gabarito }
        // Backend: { questao, resposta_correta, peso, total_questoes? }
        const gabaritoTransformado = avaliacaoData.gabarito.map((q, index) => {
          const base =
            q.questao && q.resposta_correta
              ? {
                  questao: q.questao,
                  resposta_correta: q.resposta_correta,
                  peso: q.peso || 1.0,
                }
              : {
                  questao: q.questao || `Questão ${q.numero || index + 1}`,
                  resposta_correta: q.resposta_correta || q.gabarito || "",
                  peso: q.peso || 1.0,
                };
          if (q.total_questoes != null && q.total_questoes !== "") {
            base.total_questoes = Number(q.total_questoes);
          }
          return base;
        });

        // Valida que todas as questões têm questao e resposta_correta
        for (let i = 0; i < gabaritoTransformado.length; i++) {
          const q = gabaritoTransformado[i];
          if (!q.questao || q.questao.trim() === "") {
            throw new Error(`Questão ${i + 1}: o texto da questão é obrigatório`);
          }
          if (!q.resposta_correta || q.resposta_correta.trim() === "") {
            throw new Error(`Questão ${i + 1}: a resposta correta é obrigatória`);
          }
        }

        // Prepara dados
        const criadoPorValue = user?.id || user?.email || "sistema";
        const dataToSend = {
          ...avaliacaoData,
          dataAplicacao:
            avaliacaoData.dataAplicacao instanceof Date
              ? avaliacaoData.dataAplicacao.toISOString().split("T")[0]
              : avaliacaoData.dataAplicacao,
          gabarito: gabaritoTransformado,
          criadoPor: typeof criadoPorValue === "number" ? String(criadoPorValue) : criadoPorValue,
          criadoEm: new Date().toISOString(),
        };

        const resultado = await avaliacoesService.create(dataToSend);
        return resultado;
      } catch (err) {
        const errorMessage = err.message || "Erro ao criar avaliação";
        setError(errorMessage);
        console.error("Erro ao criar avaliação:", err);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  /**
   * Lança resultados de uma avaliação para um ou mais alunos
   * @param {string} avaliacaoId - ID da avaliação
   * @param {Array<Object>} resultados - Array de resultados por aluno
   * @param {string} resultados[].alunoId - ID do aluno
   * @param {Array<boolean>} resultados[].respostas - Array de respostas (true = acerto, false = erro)
   * @param {number} [resultados[].acertos] - Número de acertos (calculado automaticamente se não fornecido)
   * @param {number} [resultados[].score] - Score percentual (calculado automaticamente se não fornecido)
   * @param {number} [resultados[].nivel] - Nível de proficiência (calculado automaticamente se não fornecido)
   * @returns {Promise<Array>} Resultados lançados
   */
  const lancarResultados = useCallback(
    async (avaliacaoId, resultados) => {
      setLoading(true);
      setError(null);

      try {
        // Busca avaliação para obter gabarito e pesos
        const avaliacao = await avaliacoesService.getById(avaliacaoId);

        if (!avaliacao) {
          throw new Error("Avaliação não encontrada");
        }

        const gabarito = avaliacao.gabarito || [];
        const pesos = gabarito.map(q => q.peso || 1);

        // Processa cada resultado
        const resultadosProcessados = resultados.map(resultado => {
          const respostas = resultado.respostas || [];

          // Calcula acertos
          let acertos = resultado.acertos;
          if (acertos === undefined || acertos === null) {
            acertos = respostas.filter(r => r === true).length;
          }

          // Calcula score
          let score = resultado.score;
          if (score === undefined || score === null) {
            score = calculateScore(acertos, gabarito.length, pesos, respostas);
          }

          // Calcula nível
          let nivel = resultado.nivel;
          if (nivel === undefined || nivel === null) {
            nivel = calculateNivel(score);
          }

          return {
            alunoId: resultado.alunoId,
            respostas,
            acertos,
            erros: gabarito.length - acertos,
            score,
            nivel,
            lancadoPor: user?.id || user?.email || "sistema",
            lancadoEm: new Date().toISOString(),
          };
        });

        const resultado = await avaliacoesService.createResultadosLote(avaliacaoId, resultadosProcessados);
        return Array.isArray(resultado) ? resultado : resultadosProcessados;
      } catch (err) {
        const errorMessage = err.message || "Erro ao lançar resultados";
        setError(errorMessage);
        console.error("Erro ao lançar resultados:", err);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user, calculateScore, calculateNivel]
  );

  /**
   * Lança acertos de diagnóstico pedagógico para alunos
   * @param {string} avaliacaoId - ID da avaliação
   * @param {Array<Object>} lancamentos - Array de acertos por aluno
   * @param {string} lancamentos[].alunoId - ID do aluno
   * @param {number} lancamentos[].acertos - Acertos do aluno
   * @param {number} [totalQuestoesOverride] - Total de questões (senão lê do gabarito)
   * @returns {Promise<Array>} Resultados lançados
   */
  const lancarNotas = useCallback(
    async (avaliacaoId, lancamentos, totalQuestoesOverride = null) => {
      setLoading(true);
      setError(null);

      try {
        const avaliacao = await avaliacoesService.getById(avaliacaoId);

        if (!avaliacao) {
          throw new Error("Avaliação não encontrada");
        }

        const totalQuestoes =
          totalQuestoesOverride != null && Number(totalQuestoesOverride) > 0
            ? Number(totalQuestoesOverride)
            : getTotalQuestoesFromGabarito(avaliacao.gabarito);

        if (!totalQuestoes || totalQuestoes <= 0) {
          throw new Error("Informe o total de questões da avaliação antes de lançar os acertos");
        }

        const resultadosExistentes = await avaliacoesService.getResultados(avaliacaoId);
        const resultadosExistentesMap = {};
        resultadosExistentes.forEach(r => {
          const alunoId = r.aluno_id || r.alunoId || (r.alunos && (r.alunos.id || r.alunos.aluno_id));
          if (alunoId) {
            resultadosExistentesMap[alunoId] = r;
          }
        });

        const resultadosParaCriar = [];
        const resultadosParaAtualizar = [];

        lancamentos.forEach(({ alunoId, acertos, nota }) => {
          // Compatibilidade: se vier `nota` (legado 0-10), trata como acertos com total 10
          let acertosNum = acertos;
          let total = totalQuestoes;
          if (acertosNum === undefined || acertosNum === null) {
            if (nota !== undefined && nota !== null) {
              acertosNum = Number(nota);
              total = 10;
            } else {
              return;
            }
          }

          acertosNum = Number(acertosNum);
          if (!Number.isFinite(acertosNum) || acertosNum < 0) return;
          if (acertosNum > total) acertosNum = total;

          const score = calculateScore(acertosNum, total);
          const nivel = calculateNivel(score);
          const erros = Math.max(0, total - acertosNum);

          const resultadoData = {
            alunoId,
            respostas: null,
            acertos: acertosNum,
            erros,
            score,
            nivel,
            lancadoPor: user?.id || user?.email || "sistema",
            lancadoEm: new Date().toISOString(),
          };

          const resultadoExistente = resultadosExistentesMap[alunoId];
          if (resultadoExistente && resultadoExistente.id) {
            resultadosParaAtualizar.push({
              id: resultadoExistente.id,
              data: resultadoData,
            });
          } else {
            resultadosParaCriar.push(resultadoData);
          }
        });

        const promises = [];

        if (resultadosParaCriar.length > 0) {
          promises.push(avaliacoesService.createResultadosLote(avaliacaoId, resultadosParaCriar));
        }

        resultadosParaAtualizar.forEach(({ id, data }) => {
          promises.push(
            avaliacoesService.updateResultado(id, {
              score: data.score,
              nivel: data.nivel,
              respostas: data.respostas,
              acertos: data.acertos,
              erros: data.erros,
            })
          );
        });

        await Promise.all(promises);

        return [...resultadosParaCriar, ...resultadosParaAtualizar.map(r => r.data)];
      } catch (err) {
        const errorMessage = err.message || "Erro ao lançar notas";
        setError(errorMessage);
        console.error("Erro ao lançar notas:", err);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user, calculateNivel, calculateScore]
  );

  /**
   * Busca evolução de proficiência de um aluno
   * @param {string} alunoId - ID do aluno
   * @param {string} [disciplina] - Disciplina (opcional)
   * @param {number} [anoLetivo] - Ano letivo (opcional)
   * @returns {Promise<Array>} Array de avaliações com evolução
   */
  const getEvolucao = useCallback(async (alunoId, disciplina = null, anoLetivo = null, useHistoricoFunction = false) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (disciplina) params.append("disciplina", disciplina);
      if (anoLetivo) params.append("anoLetivo", anoLetivo);

      const evolucao = await avaliacoesService.getEvolucao(alunoId, {
        disciplina: disciplina || undefined,
        anoLetivo: anoLetivo || undefined,
        useHistoricoFunction: useHistoricoFunction,
      });
      return Array.isArray(evolucao) ? evolucao : [];
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar evolução";
      setError(errorMessage);
      console.error("Erro ao buscar evolução:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca avaliações de uma turma
   * @param {string} turmaId - ID da turma
   * @param {string} [disciplina] - Disciplina (opcional)
   * @param {string} [tipo] - Tipo de avaliação (opcional)
   * @param {string} [atividadeEsportivaId] - ID da atividade esportiva (opcional)
   * @returns {Promise<Array>} Lista de avaliações
   */
  const getAvaliacoesByTurma = useCallback(async (turmaId, disciplina = null, tipo = null, atividadeEsportivaId = null) => {
    setLoading(true);
    setError(null);

    try {
      const avaliacoes = await avaliacoesService.getByTurma(turmaId, {
        disciplina: disciplina || undefined,
        tipo: tipo || undefined,
        atividadeEsportivaId: atividadeEsportivaId || undefined,
      });

      return Array.isArray(avaliacoes) ? avaliacoes : [];
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar avaliações";
      setError(errorMessage);
      console.error("Erro ao buscar avaliações:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cria uma nova avaliação esportiva
   * @param {Object} avaliacaoData - Dados da avaliação esportiva
   * @param {string} avaliacaoData.turmaId - ID da turma
   * @param {string} avaliacaoData.tipo - Tipo (ENTRADA, PERIODICA, SAIDA)
   * @param {string} avaliacaoData.atividadeEsportivaId - ID da atividade esportiva
   * @param {string} avaliacaoData.titulo - Título da avaliação
   * @param {string} avaliacaoData.descricao - Descrição opcional
   * @param {Date|string} avaliacaoData.dataAplicacao - Data de aplicação
   * @returns {Promise<Object>} Avaliação criada
   */
  const createAvaliacaoEsportiva = useCallback(
    async avaliacaoData => {
      setLoading(true);
      setError(null);

      try {
        // Validações
        if (!avaliacaoData.turmaId) {
          throw new Error("Turma é obrigatória");
        }
        if (!avaliacaoData.tipo || !Object.values(TIPOS_AVALIACAO).includes(avaliacaoData.tipo)) {
          throw new Error("Tipo de avaliação inválido");
        }
        if (!avaliacaoData.atividadeEsportivaId) {
          throw new Error("Atividade esportiva é obrigatória");
        }

        // Prepara dados
        const criadoPorValue = user?.id || user?.email || "sistema";
        const dataToSend = {
          ...avaliacaoData,
          dataAplicacao:
            avaliacaoData.dataAplicacao instanceof Date
              ? avaliacaoData.dataAplicacao.toISOString().split("T")[0]
              : avaliacaoData.dataAplicacao,
          criadoPor: typeof criadoPorValue === "number" ? String(criadoPorValue) : criadoPorValue,
          criadoEm: new Date().toISOString(),
        };

        const resultado = await avaliacoesService.createEsportiva(dataToSend);
        return resultado;
      } catch (err) {
        const errorMessage = err.message || "Erro ao criar avaliação esportiva";
        setError(errorMessage);
        console.error("Erro ao criar avaliação esportiva:", err);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  /**
   * Lança avaliações esportivas (performance e comportamento) para alunos
   * @param {string} avaliacaoId - ID da avaliação
   * @param {Array<Object>} avaliacoes - Array de avaliações por aluno
   * @param {string} avaliacoes[].alunoId - ID do aluno
   * @param {string} avaliacoes[].performance - Performance (EXCELENTE, BOM, REGULAR, PRECISA_MELHORAR)
   * @param {string} avaliacoes[].comportamento - Comportamento (EXCELENTE, BOM, REGULAR, PRECISA_MELHORAR)
   * @returns {Promise<Array>} Resultados lançados
   */
  const lancarAvaliacaoEsportiva = useCallback(
    async (avaliacaoId, avaliacoes) => {
      setLoading(true);
      setError(null);

      try {
        // Busca avaliação para validar
        const avaliacao = await avaliacoesService.getById(avaliacaoId);

        if (!avaliacao) {
          throw new Error("Avaliação não encontrada");
        }

        // Busca resultados existentes para verificar quais precisam ser atualizados
        const resultadosExistentes = await avaliacoesService.getResultados(avaliacaoId);
        const resultadosExistentesMap = {};
        resultadosExistentes.forEach(r => {
          const alunoId = r.aluno_id || r.alunoId || (r.alunos && (r.alunos.id || r.alunos.aluno_id));
          if (alunoId) {
            resultadosExistentesMap[alunoId] = r;
          }
        });

        // Processa cada avaliação
        const resultadosParaCriar = [];
        const resultadosParaAtualizar = [];

        avaliacoes.forEach(({ alunoId, performance, comportamento }) => {
          const resultadoData = {
            alunoId,
            performance,
            comportamento,
            lancadoPor: user?.id || user?.email || "sistema",
            lancadoEm: new Date().toISOString(),
          };

          // Verifica se já existe resultado para este aluno
          const resultadoExistente = resultadosExistentesMap[alunoId];
          if (resultadoExistente && resultadoExistente.id) {
            // Atualiza resultado existente
            resultadosParaAtualizar.push({
              id: resultadoExistente.id,
              data: resultadoData,
            });
          } else {
            // Cria novo resultado
            resultadosParaCriar.push(resultadoData);
          }
        });

        // Executa criações e atualizações
        const promises = [];

        // Cria novos resultados
        if (resultadosParaCriar.length > 0) {
          promises.push(avaliacoesService.createResultadosEsportivosLote(avaliacaoId, resultadosParaCriar));
        }

        // Atualiza resultados existentes
        resultadosParaAtualizar.forEach(({ id, data }) => {
          promises.push(
            avaliacoesService.updateResultado(id, {
              performance: data.performance,
              comportamento: data.comportamento,
            })
          );
        });

        await Promise.all(promises);

        return [...resultadosParaCriar, ...resultadosParaAtualizar.map(r => r.data)];
      } catch (err) {
        const errorMessage = err.message || "Erro ao lançar avaliações esportivas";
        setError(errorMessage);
        console.error("Erro ao lançar avaliações esportivas:", err);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  /**
   * Busca uma avaliação específica por ID
   * @param {string} avaliacaoId - ID da avaliação
   * @returns {Promise<Object>} Avaliação
   */
  const getAvaliacaoById = useCallback(async avaliacaoId => {
    setLoading(true);
    setError(null);

    try {
      const avaliacao = await avaliacoesService.getById(avaliacaoId);
      return avaliacao;
    } catch (err) {
      const errorMessage = err.message || "Erro ao buscar avaliação";
      setError(errorMessage);
      console.error("Erro ao buscar avaliação:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Busca resultados de uma avaliação
   * @param {string} avaliacaoId - ID da avaliação
   * @returns {Promise<Array>} Lista de resultados
   */
  const getResultadosByAvaliacao = useCallback(async avaliacaoId => {
    setLoading(true);
    setError(null);

    try {
      const resultados = await avaliacoesService.getResultados(avaliacaoId);
      return Array.isArray(resultados) ? resultados : [];
    } catch (err) {
      const status = err?.status || err?.statusCode || err?.response?.status;
      if (status === 403 || status === 401) {
        const permissionMessage =
          "Você não tem permissão para acessar os resultados desta avaliação. Verifique seu vínculo com o núcleo da turma.";
        setError(permissionMessage);
        console.error("Erro ao buscar resultados (RLS/perm):", err);
        throw new Error(permissionMessage);
      }

      const errorMessage = err.message || "Erro ao buscar resultados";
      setError(errorMessage);
      console.error("Erro ao buscar resultados:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Atualiza uma avaliação
   * @param {string} avaliacaoId - ID da avaliação
   * @param {Object} avaliacaoData - Dados atualizados
   * @returns {Promise<Object>} Avaliação atualizada
   */
  const updateAvaliacao = useCallback(
    async (avaliacaoId, avaliacaoData) => {
      setLoading(true);
      setError(null);

      try {
        const dataToSend = {
          ...avaliacaoData,
          atualizadoPor: user?.id || user?.email || "sistema",
          atualizadoEm: new Date().toISOString(),
        };

        const resultado = await avaliacoesService.update(avaliacaoId, dataToSend);
        return resultado;
      } catch (err) {
        const errorMessage = err.message || "Erro ao atualizar avaliação";
        setError(errorMessage);
        console.error("Erro ao atualizar avaliação:", err);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  /**
   * Deleta uma avaliação
   * @param {string} avaliacaoId - ID da avaliação
   * @returns {Promise<boolean>} true se deletado com sucesso
   */
  const deleteAvaliacao = useCallback(async avaliacaoId => {
    setLoading(true);
    setError(null);

    try {
      await avaliacoesService.delete(avaliacaoId);
      return true;
    } catch (err) {
      const errorMessage = err.message || "Erro ao deletar avaliação";
      setError(errorMessage);
      console.error("Erro ao deletar avaliação:", err);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Limpa o estado de erro
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createAvaliacao,
    createAvaliacaoEsportiva,
    lancarResultados,
    lancarNotas,
    lancarAvaliacaoEsportiva,
    calculateNivel,
    calculateScore,
    getEvolucao,
    getAvaliacoesByTurma,
    getAvaliacaoById,
    getResultadosByAvaliacao,
    updateAvaliacao,
    deleteAvaliacao,
    clearError,
    // Constantes exportadas
    TIPOS_AVALIACAO,
    DISCIPLINAS,
    NIVELS_PROFICIENCIA,
  };
};

export default useAvaliacoes;
